import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, notificationApi } from '../api';

const TYPE_LABEL: Record<string, { label: string; color: string }> = {
  system: { label: '系统', color: 'bg-slate-100 text-slate-600' },
  order: { label: '订单', color: 'bg-blue-100 text-blue-700' },
  stock: { label: '库存', color: 'bg-orange-100 text-orange-700' },
  attendance: { label: '考勤', color: 'bg-teal-100 text-teal-700' },
  portal: { label: '客户门户', color: 'bg-indigo-100 text-indigo-700' },
};

export default function Notifications() {
  const nav = useNavigate();
  const [list, setList] = useState<any[]>([]);
  const [unread, setUnread] = useState(0);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [canSend, setCanSend] = useState(false);
  const [depts, setDepts] = useState<any[]>([]);

  // 群发表单
  const [showComposer, setShowComposer] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', targetType: 'all', department_id: '' });

  const load = () => {
    notificationApi.list(onlyUnread || undefined).then(setList).catch(() => {});
    notificationApi.count().then((d: any) => setUnread(d.unread)).catch(() => {});
  };
  useEffect(load, [onlyUnread]);

  useEffect(() => {
    api('/auth/me').then((d) => setCanSend((d.permissions || []).includes('notify:create'))).catch(() => {});
    if (canSend) api('/departments').then(setDepts).catch(() => {});
  }, [canSend]);

  const onRead = async (n: any) => {
    if (!n.is_read) {
      await notificationApi.read(n.id).catch(() => {});
      load();
    }
    if (n.link) nav(n.link);
  };
  const onReadAll = async () => { await notificationApi.readAll().catch(() => {}); load(); };
  const onDelete = async (id: number) => { await notificationApi.remove(id).catch(() => {}); load(); };

  const send = async () => {
    if (!form.title) return alert('请输入通知标题');
    const target = form.targetType === 'dept'
      ? { type: 'dept', department_id: Number(form.department_id) || null }
      : { type: 'all' };
    try {
      await notificationApi.send({ title: form.title, content: form.content, target });
      setForm({ title: '', content: '', targetType: 'all', department_id: '' });
      setShowComposer(false);
      load();
      alert('通知已发送');
    } catch (e: any) { alert(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">消息通知</h2>
          <p className="text-sm text-slate-500 mt-1">业务事件自动提醒 + 管理员群发通知</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOnlyUnread(!onlyUnread)} className={`rounded-lg px-3 py-1.5 text-sm border ${onlyUnread ? 'bg-brand-50 border-brand-300 text-brand-700' : 'border-slate-200 hover:bg-slate-50'}`}>
            {onlyUnread ? '✓ 仅看未读' : '全部'}
          </button>
          <button onClick={onReadAll} className="rounded-lg px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50">全部已读</button>
          {canSend && (
            <button onClick={() => { setShowComposer(!showComposer); if (!depts.length) api('/departments').then(setDepts).catch(() => {}); }} className="bg-brand-600 text-white rounded-lg px-3 py-1.5 text-sm hover:bg-brand-700">
              ✉️ 群发通知
            </button>
          )}
        </div>
      </div>

      {/* 群发表单 */}
      {showComposer && (
        <div className="bg-white rounded-xl shadow p-4 space-y-3">
          <div className="font-medium text-sm">发送新通知</div>
          <input className="w-full border rounded-lg px-3 py-2" placeholder="通知标题 *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea className="w-full border rounded-lg px-3 py-2" rows={3} placeholder="通知内容" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          <div className="flex gap-2 items-center flex-wrap">
            <select className="border rounded-lg px-3 py-2 text-sm" value={form.targetType} onChange={(e) => setForm({ ...form, targetType: e.target.value })}>
              <option value="all">全员广播</option>
              <option value="dept">按部门</option>
            </select>
            {form.targetType === 'dept' && (
              <select className="border rounded-lg px-3 py-2 text-sm" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                <option value="">选择部门</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            <button onClick={send} className="ml-auto bg-brand-600 text-white rounded-lg px-4 py-2 text-sm hover:bg-brand-700">发送</button>
          </div>
        </div>
      )}

      {/* 列表 */}
      <div className="space-y-2">
        {list.map((n) => (
          <div
            key={n.id}
            onClick={() => onRead(n)}
            className={`bg-white rounded-xl shadow p-4 flex items-start gap-3 cursor-pointer hover:shadow-md transition ${n.is_read ? 'opacity-70' : 'border-l-4 border-brand-500'}`}
          >
            <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap mt-0.5 ${TYPE_LABEL[n.type]?.color || 'bg-slate-100'}`}>{TYPE_LABEL[n.type]?.label || n.type}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm flex items-center gap-2">
                {n.title}
                {!n.is_read && <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />}
              </div>
              {n.content && <div className="text-sm text-slate-500 mt-0.5">{n.content}</div>}
              <div className="text-xs text-slate-400 mt-1">{(n.created_at || '').replace('T', ' ').slice(0, 19)}</div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); onDelete(n.id); }} className="text-red-400 hover:text-red-600 text-xs">删除</button>
          </div>
        ))}
        {list.length === 0 && <div className="bg-white rounded-xl shadow p-10 text-center text-slate-400">暂无消息</div>}
      </div>
    </div>
  );
}
